if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        
        // Bind the click handler
        this.handleSlideClick = this.handleSlideClick.bind(this);
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.containerWidth = this.dataset.container;
        this.cleanupScrollAnimations = [];
        this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
        this.borderRadius = this.dataset.borderRadius;
        this.zoom = 0;

        if (!this.enableSlider) return;

        this.setupZoom();
        window.addEventListener('resize', this.setupZoom.bind(this));
        
        // Bubble phase: allows Swiper's native controls to work properly
        this.addEventListener('click', this.handleSlideClick, false);

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      setupZoom() {
        if (!this.enableZoom) return;
        this.destroyZoom();
        const pageWidthEl = this.querySelector('.page-width');
        const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
        this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);
        if (this.zoom > 15) {
          this.initZoom();
        }
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          simulateTouch: true,
          threshold: 20,
          effect: 'fade',
          // UPDATED: These must be true to allow your custom click handler to work
          preventClicks: true,
          preventClicksPropagation: true,
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();
        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement);

          // Scroll-triggered sliding: start sliding when banner is scrolled into view
          if (autoplayDelay > 0 && 'IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  if (this.sliderInstance.slider && this.sliderInstance.slider.autoplay && typeof this.sliderInstance.slider.autoplay.start === 'function') {
                    this.sliderInstance.slider.autoplay.start();
                  }
                } else {
                  if (this.sliderInstance.slider && this.sliderInstance.slider.autoplay && typeof this.sliderInstance.slider.autoplay.stop === 'function') {
                    this.sliderInstance.slider.autoplay.stop();
                  }
                }
              });
            }, { threshold: 0.15 });
            observer.observe(this);
          }
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;
        this.updateControlsScheme(slides[activeIndex]);
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      handleSlideClick(e) {
        // Let Swiper's own controls handle themselves
        if (e.target.closest('.swiper-controls, .swiper-pagination, .swiper-button')) {
          return;
        }

        // Let overlay category CTAs handle themselves
        if (e.target.closest('.slideshow-overlay-btn, .slideshow-overlay-buttons a')) {
          return;
        }

        e.stopImmediatePropagation();

        const slide = e.target.closest('.swiper-slide');
        if (!slide) return;

        const interactiveSelectors = 'button, input, .slideshow__button, .slideshow-overlay-btn';
        if (e.target.closest(interactiveSelectors)) return;

        const link = slide.querySelector('.slide-absolute-link');
        if (link && link.href) {
          window.location.href = link.href;
        }
      }

      destroyZoom() {
        this.cleanupScrollAnimations.forEach((fn) => fn());
        this.cleanupScrollAnimations = [];
        this.style.clipPath = 'none';
        if (this.sliderControls) this.sliderControls.style.bottom = 0;
      }
    }
  );
}